import React, { useEffect, useState, useCallback } from "react";
import { IAnalysisSummaryResponse } from "../../../../interface/analysisSummary";
import { REIProgressSpinner } from "../../../../components/ui";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../store";
import EditableDataTable from "../../../../components/shared/editableDataTable";
import { triggerReload } from "../../../../store/slice/analyserSlice";
import { ForecastItemCode, MEMBER_KEY, USER_DETAIL, YEARS } from "../../../../utils/constants";
import { httpUpdate } from "../../../../services/httpService";
import { ApiEndPoints } from "../../../../utils/endpoints";

const AnalysisOverview: React.FC<{
  editFlag: boolean;
  visibleYears: { label: string; defaultShow: boolean }[];
  resetEditFlag: () => void;
  isfromPortfolioTracker?: boolean;
  reloadEditableDataTable?: () => void;
}> = ({ editFlag, visibleYears, resetEditFlag, isfromPortfolioTracker = false, reloadEditableDataTable }) => {
  const analysisSummary = useSelector((state: RootState) => state.analyser.analysisSummary as IAnalysisSummaryResponse);
  const [overviewData, setOverviewData] = useState<{ [key: string]: any }>({});
  const [loading, setLoading] = useState(false);
  const memberDetail = JSON.parse(localStorage.getItem(USER_DETAIL) || "{}");
  const dispatch = useDispatch();

  const didValueChange = useCallback(
    (itemCode: string, year: number) =>
      analysisSummary?.ForecastOverrides?.some(
        (forecast) => forecast.ForecastItemCode === itemCode && forecast.Year === year
      ),
    [analysisSummary]
  );

  const didAnyValueChange = useCallback(
    (itemCode: string) =>
      analysisSummary?.ForecastOverrides?.some((forecast) => forecast.ForecastItemCode === itemCode),
    [analysisSummary]
  );

  const forectedForFutureYear = useCallback(
    (itemCode: string, year: number) =>
      analysisSummary?.ForecastOverrides?.some(
        (forecast) => forecast.ForecastItemCode === itemCode && forecast.Year === year && forecast.ThisYearOnly === 0
      ),
    [analysisSummary]
  );

  const mapValuesToYears = (
    overview: { [key: string]: any },
    forecastCode: string,
    prefix: string,
    editableYears: number[],
    displayForecastButton: boolean = true
  ) => {
    const yearsMap: {
      [key: string]: { value: string; editable: boolean; didValueChange: boolean; forcastedForFutureYears: boolean };
    } = {};
    YEARS.forEach((year, index) => {
      const yearLabel = year.label.replace("Year ", "Yr");
      yearsMap[year.label] = {
        value: overview[`${prefix}${yearLabel}`]?.toString() || "",
        editable: editableYears?.includes(index),
        didValueChange: displayForecastButton ? didValueChange(forecastCode, index) : false,
        forcastedForFutureYears: forectedForFutureYear(forecastCode, index),
      };
    });
    return yearsMap;
  };

  var initializeOverviewData = (analysisSummary: IAnalysisSummaryResponse) => ({
    "Capital growth rate %": {
      type: "%",
      forecastOverrideKey: ForecastItemCode.CapitalGrowth,
      anyCellValueChange: didAnyValueChange(ForecastItemCode.CapitalGrowth),
      values: mapValuesToYears(
        analysisSummary.AnalysisOverview,
        ForecastItemCode.CapitalGrowth,
        "cgr",
        [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
      ),
    },
    "Consumer price index %": {
      type: "%",
      forecastOverrideKey: "CashflowDetails.CPI",
      anyCellValueChange: didAnyValueChange(ForecastItemCode.CPI),
      values: mapValuesToYears(
        analysisSummary.AnalysisOverview,
        ForecastItemCode.CPI,
        "cpi",
        [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
      ),
    },
    "Property market value $": {
      type: "currency",
      forecastOverrideKey: ForecastItemCode.PropertyMarketValue,
      anyCellValueChange: didAnyValueChange(ForecastItemCode.PropertyMarketValue),
      values: mapValuesToYears(
        analysisSummary.AnalysisOverview,
        ForecastItemCode.PropertyMarketValue,
        "marketValue",
        [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
        false
      ),
    },
    "Amount of loan $": {
      type: "currency",
      values: mapValuesToYears(analysisSummary.AnalysisOverview, "", "AmountLoan", []),
    },
  });

  useEffect(() => {
    if (analysisSummary) {
      setOverviewData(initializeOverviewData(analysisSummary));
    }
  }, [analysisSummary]);

  const onEdit = async (rowKey: string, year: string, value: string, forecastItemCode: string | undefined) => {
    const previousValue = overviewData[rowKey].values[year].value;
    if (parseFloat(previousValue) === parseFloat(value)) {
      return;
    }
    setLoading(true);
    const variableBody = {
      MemberId: localStorage.getItem(MEMBER_KEY),
      MemberCountryCode: memberDetail.Country,
      LVR: analysisSummary?.Levers?.LoanValueRatio,
      LoansPrincipalInterest: analysisSummary?.LoanPrincipalInterest,
      ForecastOverrides: [
        {
          ForecastItemCode: forecastItemCode,
          Year: year.replace("Year ", "").trim(),
          ThisYearOnly: 1,
          Value: parseFloat(value).toString(),
          Remove: 0,
          LoanId: 0,
        },
      ],
      ...(isfromPortfolioTracker
        ? { PropertyTrackerId: analysisSummary?.PortfolioTrackerId }
        : { PropertyAnalyserId: analysisSummary?.PropertyAnalyserId }),
    };
    updateForecastValue(variableBody, true);
    setOverviewData((prevData) => ({
      ...prevData,
      [rowKey]: {
        ...prevData[rowKey],
        values: {
          ...prevData[rowKey].values,
          [year]: { ...prevData[rowKey].values[year], value },
        },
      },
    }));
  };

  const onForecastEdit = async (rowKey: string, year: string, value: string, forecastItemCode: string) => {
    setLoading(true);
    let variableBody = {};
    let forecastOverrides = analysisSummary.ForecastOverrides || [];
    if (year === "Year 10") {
      forecastOverrides = analysisSummary.ForecastOverrides.filter(
        (forecast) => forecast.ForecastItemCode === forecastItemCode
      );
    }
    variableBody = {
      MemberId: localStorage.getItem(MEMBER_KEY),
      MemberCountryCode: memberDetail.Country,
      LVR: analysisSummary?.Levers?.LoanValueRatio,
      LoansPrincipalInterest: analysisSummary?.LoanPrincipalInterest,
      ForecastOverrides:
        year === "Year 10"
          ? forecastOverrides.map((forecast) => {
              return {
                ...forecast,
                Remove: 1,
              };
            })
          : [
              {
                ForecastItemCode: forecastItemCode,
                Year: year.replace("Year ", "").trim(),
                ThisYearOnly: 0,
                Value: parseFloat(value).toString(),
                Remove: 0,
                LoanId: 0,
              },
            ],
      ...(isfromPortfolioTracker
        ? { PropertyTrackerId: analysisSummary?.PortfolioTrackerId }
        : { PropertyAnalyserId: analysisSummary?.PropertyAnalyserId }),
    };
    updateForecastValue(variableBody, false);
  };

  const updateForecastValue = async (variableBody: object, updateSingleValue: boolean) => {
    try {
      let result;
      if (isfromPortfolioTracker) {
        result = await httpUpdate(`${ApiEndPoints.tracker}/PortfolioTracker/Forecast/`, variableBody);
      } else {
        result = await httpUpdate(`${ApiEndPoints.analyser}/AnalysisReport/`, variableBody);
      }

      if (result.status === 200 && result.data) {
        dispatch(triggerReload());

        setOverviewData(initializeOverviewData(result.data));
        if (!updateSingleValue && reloadEditableDataTable) {
          reloadEditableDataTable();
        }
      }
    } catch (error) {
      console.error("Error updating loan value ratio:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!analysisSummary || loading) {
    return (
      <div className="w-full text-center">
        <REIProgressSpinner />
      </div>
    );
  }

  return (
    overviewData && (
      <EditableDataTable
        title="Overview"
        data={overviewData}
        years={visibleYears.map((year) => year.label)}
        isEditable={editFlag}
        onForecastValueChange={onForecastEdit}
        onEdit={onEdit}
      />
    )
  );
};

export default AnalysisOverview;
